// The shared backlit "cap face" visual vocabulary. The design rule is that mouse zones behave
// exactly like keycaps, so Keycap and MouseMap paint the same surfaces. Keeping that recipe in one
// place stops the two from silently drifting (they had: the unlit bottom stop was 0.325 on caps but
// 0.33 on zones, a slip carried over from the v2 design source itself).
//
// Only the genuinely-shared pieces live here. The outer-halo BLUR is intentionally different (caps
// glow a touch wider than zones), so each component keeps its own halo string and only shares the
// haloPct strength below.

/** A bound, lit backlit surface: the Category glow bled into the dark cap (v2 keylens-keyboard.jsx). */
export const backlitBoundSurface = (glow: string): string =>
  `linear-gradient(180deg, color-mix(in oklch, ${glow} 18%, oklch(0.40 0.012 261)), color-mix(in oklch, ${glow} 8%, oklch(0.315 0.012 261)))`;

// An unbound, unlit backlit cap: a dark cap on the charcoal plate. Unified on the caps value
// (0.325), which the v2 README documents and the "zones behave exactly like keycaps" rule
// requires; the v2 mouse source's 0.33 was a slip.
export const BACKLIT_UNLIT_SURFACE =
  "linear-gradient(180deg, oklch(0.40 0.01 261), oklch(0.325 0.01 261))";

/** The unlit legend colour on an unbound backlit cap/zone. */
export const BACKLIT_UNLIT_LEGEND = "oklch(0.62 0.008 260)";

/**
 * The backlit surface + legend colour, the one face decision Keycap and MouseMap must agree on: a
 * bound face glows its Category (the lit cap), an unbound one is the dark unlit cap. Deliberately the
 * ONLY shared piece - the border tint and the outer-halo blur stay per-component (see the note at
 * the top of this file: caps and zones tune those differently on purpose).
 */
export function backlitFace(
  bound: boolean,
  colors: { glow: string; lit: string } | null | undefined,
): { surface: string; legend: string } {
  return bound && colors
    ? { surface: backlitBoundSurface(colors.glow), legend: colors.lit }
    : { surface: BACKLIT_UNLIT_SURFACE, legend: BACKLIT_UNLIT_LEGEND };
}

/** The pressed surface when a cap/zone has no Category accent to fill with (depress fallback). */
export const pressedSurface = (accent: string | undefined, backlit: boolean): string =>
  accent ?? (backlit ? "oklch(0.47 0.01 261)" : "oklch(0.92 0.01 250)");

/** Outer-halo strength as a color-mix percent, scaled by keyGlow and clamped 0..58. */
export const haloPct = (keyGlow: number): number =>
  Math.max(0, Math.min(58, Math.round(34 * keyGlow)));

/** The dim state shared by the board and mouse: the flashlight wins over isolate/fallback. */
export interface DimState {
  /** Category ids kept lit while isolating; bound items outside them dim. */
  dimCategories: string[] | null;
  /** The search/hover flashlight: when set, every item whose code is NOT listed dims. */
  litCodes: string[] | null;
  /** While a non-empty combo is held, dim base-fallback items. */
  dimFallback: boolean;
}

/** The minimal shape isDimmed reads off a keycap or a mouse zone. A keycap's active code may be
 * null (a dead cap); a mouse zone's never is. */
export interface Dimmable {
  code: string | null;
  binding: { category: string } | null;
  /** The combo that defines this binding ("" base); provenance. */
  sourceCombo?: string;
}

/**
 * Whether an item dims. The flashlight is authoritative when active (spotlight its matches, dim the
 * rest); with no flashlight, an isolated Category (bound items only) or a held-combo base
 * fallback dims. One predicate so the board (Keyboard) and the mouse (MouseMap) agree.
 */
export function isDimmed(item: Dimmable, dim: DimState): boolean {
  // a dead cap can never be a flashlight match, so it dims with the rest
  if (dim.litCodes != null) return !(item.code != null && dim.litCodes.includes(item.code));
  return (
    (dim.dimCategories != null &&
      item.binding != null &&
      !dim.dimCategories.includes(item.binding.category)) ||
    (dim.dimFallback && item.sourceCombo === "")
  );
}
